import { feedGraphQLClient } from '@edusense/feed-client';
import { ACCESS_TOKEN_KEY, TOKEN_KEY, TOKEN_TYPE_KEY } from '@edusense/shared-constants';
// 🔧 Resolve auth token from localStorage (same as social-client & feed-client)
function resolveAuthHeaderFromStorage() {
    try {
        if (typeof window === 'undefined') return '';
        const ls = window.localStorage;
        const ss = window.sessionStorage;
        const tryKeys = [
            ACCESS_TOKEN_KEY,
            TOKEN_KEY,
            'accessToken',
            'edusense_token',
            'auth_token',
            'reward_accessToken'
        ].filter(Boolean);
        let token = null;
        for (const k of tryKeys){
            token = ls && ls.getItem(k) || ss && ss.getItem(k);
            if (token) break;
        }
        // Fallback: scan any key that ends with 'accessToken'
        if (!token && ls) {
            for(let i = 0; i < ls.length; i++){
                const key = ls.key(i) || '';
                if (key === 'auth_access_token') continue;
                if (key.toLowerCase().endsWith('accesstoken')) {
                    const v = ls.getItem(key);
                    if (v) {
                        token = v;
                        break;
                    }
                }
            }
        }
        const tokenType = ls && (ls.getItem(TOKEN_TYPE_KEY) || ls.getItem('token_type')) || ss && ss.getItem('token_type') || 'Bearer';
        return token ? `${tokenType || 'Bearer'} ${token}` : '';
    } catch  {
        return '';
    }
}
// GraphQL Query 문자열
const GET_USER_TWEETS_QUERY = `
  query GetUserTweets($userId: ID!, $first: Int, $after: String) {
    userTweets(userId: $userId, first: $first, after: $after) {
      edges {
        node {
          id
          content
          contentType
          mediaUrls
          category
          hashtags
          createdAt
          updatedAt
          author {
            id
            username
            displayName
            avatarUrl
            isVerified
          }
          engagement {
            likes
            comments
            shares
            views
            retweets
            replies
            quotes
          }
          viewerHasLiked
          viewerHasShared
          viewerCanEdit
          viewerCanDelete
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;
/**
 * GraphQL로 트윗 데이터 가져오기
 * @param userId 사용자 ID
 * @param first 페이지 크기
 * @param after 커서 (페이지네이션)
 */ export const fetchTweetsFromGraphQL = async (userId, first = 20, after)=>{
    console.log('[useTweets.api] GraphQL 호출 파라미터:', {
        userId,
        first,
        after
    });
    const graphqlEndpoint = process.env.REACT_APP_FEED_GRAPHQL_URL || 'https://api.ugot.uk/api/feed/graphql';
    try {
        // ✅ Use feedGraphQLClient instead of raw request() to get auth headers
        const response = await feedGraphQLClient.request(GET_USER_TWEETS_QUERY, {
            userId,
            first,
            after
        });
        if (!response || !response.userTweets) {
            throw new Error('GraphQL 응답 데이터가 없습니다.');
        }
        console.log('[useTweets.api] GraphQL 응답:', {
            edgesCount: response.userTweets.edges.length,
            hasNextPage: response.userTweets.pageInfo.hasNextPage,
            totalCount: response.userTweets.totalCount
        });
        return response;
    } catch (error) {
        console.error('[useTweets.api] GraphQL 에러:', error);
        throw error;
    }
};
/**
 * 트윗 API 요청 파라미터 생성 (GraphQL용)
 * @param params 요청 파라미터
 * @param userId 사용자 ID
 * @param pageSize 페이지 크기
 * @returns GraphQL 요청 파라미터
 */ export const buildTweetGraphQLParams = (params, userId, pageSize, cursor)=>{
    return {
        userId: userId ? String(userId) : '',
        first: pageSize,
        after: cursor
    };
};
// GraphQL Query for HOME Feed (forYouFeed)
const FOR_YOU_FEED_QUERY = `
  query ForYouFeed($userId: ID, $first: Int, $after: String) {
    forYouFeed(userId: $userId, first: $first, after: $after) {
      edges {
        node {
          id
          content
          contentType
          mediaUrls
          category
          hashtags
          createdAt
          updatedAt
          author {
            id
            username
            displayName
            avatarUrl
            isVerified
          }
          engagement {
            likes
            comments
            shares
            views
            retweets
            replies
            quotes
          }
          viewerHasLiked
          viewerHasShared
          viewerCanEdit
          viewerCanDelete
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;
/**
 * GraphQL로 HOME 피드 (For You Feed) 가져오기
 * @param first 페이지 크기
 * @param after 커서 (페이지네이션)
 * @param userId 선택적 사용자 ID (로그인 시 맞춤형 피드)
 */ export const fetchHomeFeed = async (first = 20, after, userId)=>{
    console.log('[useTweets.api] HOME Feed GraphQL 호출:', {
        userId,
        first,
        after
    });
    try {
        // ✅ Use feedGraphQLClient instead of raw request() to get auth headers
        const response = await feedGraphQLClient.request(FOR_YOU_FEED_QUERY, {
            userId,
            first,
            after
        });
        if (!response || !response.forYouFeed) {
            throw new Error('GraphQL forYouFeed 응답 데이터가 없습니다.');
        }
        console.log('[useTweets.api] HOME Feed 응답:', {
            edgesCount: response.forYouFeed.edges.length,
            hasNextPage: response.forYouFeed.pageInfo.hasNextPage,
            totalCount: response.forYouFeed.totalCount
        });
        // REST API 형식으로 변환 (기존 useTweets.ts 호환)
        return {
            tweets: response.forYouFeed.edges.map((edge)=>edge.node),
            hasMore: response.forYouFeed.pageInfo.hasNextPage,
            endCursor: response.forYouFeed.pageInfo.endCursor,
            totalCount: response.forYouFeed.totalCount
        };
    } catch (error) {
        console.error('[useTweets.api] HOME Feed GraphQL 에러:', error);
        throw error;
    }
};
